/**
 * QuickActionsRow — large pill cards directly under the greeting.
 *
 * The Home tab is a navigation hub first; this row is the primary "where do
 * I want to go" affordance. Touch-friendly (52px+) so mobile works the same
 * as desktop. Each tile has icon + label + tagline.
 *
 * Bill 2026-05-13 (v0.14.43) — canonical SPEC Screen 32 labels:
 *   - Start new cart   → /rao              (was "New application")
 *   - Resume draft     → /rao/drafts       (NEW — was "Add to lobby")
 *   - Search customer  → Ctrl+K palette    (label tightened from "Find customer")
 *   - Find task        → /rtm              (NEW — was "Today's appointments")
 *
 * The retired actions (Add to lobby + Today's appts) now live as drill-route
 * destinations from the MetricCardsRow (Today's Appts card) and from the
 * dedicated Lobby tab.
 */
import { useNavigate } from 'react-router';
import {
  Plus,
  Wrench,
  Search,
  ClipboardList,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface QuickAction {
  key: string;
  icon: LucideIcon;
  label: string;
  tagline: string;
  onClick: () => void;
  /** Optional accent class — drives the icon color stripe. */
  accent: 'blue' | 'green' | 'purple' | 'amber';
}

export function QuickActionsRow(): JSX.Element {
  const navigate = useNavigate();

  // The three prominent officer actions Bill named (Item 5): New Account,
  // Find Customer, Account Maintenance — plus Find task.
  const actions: ReadonlyArray<QuickAction> = [
    {
      key: 'start-cart',
      icon: Plus,
      label: 'New Account',
      tagline: 'Open new account · personal, business, trust',
      // Straight into the new cart's People screen (Bill 2026-07-13 Item 7)
      // — /rao/new bootstraps the cart and lands on People, no extra click.
      onClick: () => navigate('/rao/new'),
      accent: 'blue',
    },
    {
      key: 'find-customer',
      icon: Search,
      label: 'Find Customer',
      tagline: 'Look up by name, SSN, or CIF · full record + accounts',
      // Opens the real global customer lookup modal (Item 6), NOT the
      // module-jump palette.
      onClick: () => window.dispatchEvent(new CustomEvent('rop:find-customer')),
      accent: 'purple',
    },
    // PILOT 2026-09-04: "Account Maintenance" (/ram) and "Find task" (/rtm)
    // removed for the FSR pilot. Both modules are hidden from the rail because
    // they are not wired end to end for Phase 1, and hiddenFromRail does not
    // reach in-page navigate() links — this tile row would have been a live
    // doorway into a hidden module. Restore alongside the modules.ts flags.
  ];

  return (
    <section className="home-quick" aria-label="Quick actions">
      {actions.map((a) => {
        const Icon = a.icon;
        return (
          <button
            key={a.key}
            type="button"
            className={`home-quick__tile home-quick__tile--${a.accent}`} data-tour={`home-${a.key}`}
            onClick={a.onClick}
          >
            <span className="home-quick__icon" aria-hidden>
              <Icon size={22} />
            </span>
            <span className="home-quick__text">
              <span className="home-quick__label">{a.label}</span>
              <span className="home-quick__tagline">{a.tagline}</span>
            </span>
          </button>
        );
      })}
    </section>
  );
}
